import { AlgoliaHit } from '../../models';
import {
    Link,
    M30ProductCarouselModule,
    M31ProductCarouselModule,
    M32ProductCarouselModule,
} from '~/lib/data-contract';
import { useModule } from '~/templates';
import ContentSlider from '~/shared/components/content-slider/content-slider.component';
import { useFranchise } from '~/shared/utils';
import { useSource } from '~/shared/utils/gtm';
import { mapFromAlgolia } from '~/features/productsList/utils';
import { memo, useMemo } from 'react';
import { ProductCard } from '~/shared/components/product-card/product-card.component';
import ProductCarouselSkeleton from '../product-carousel-skeleton/product-carousel-skeleton.component';

export type Props = {
    hits?: AlgoliaHit[];
    isLoading?: boolean;
};

const ProductCarousel = memo(({ hits, isLoading = true }: Props) => {
    const {
        element: { headline, link },
    } = useModule<M30ProductCarouselModule | M31ProductCarouselModule | M32ProductCarouselModule>();

    const franchise = useFranchise();
    const { source } = useSource();

    const formattedHits = useMemo(
        () =>
            hits?.map((product) =>
                mapFromAlgolia(product, franchise, product.__position as unknown as number)
            ) ?? [],
        [hits, franchise]
    );

    // If isLoading and hits is undefined, show loading state
    if (isLoading && !hits) {
        return <ProductCarouselSkeleton />;
    }

    // If there are results, show the carousel
    if (formattedHits.length > 0) {
        return (
            <ContentSlider headline={headline} link={link as Link} hasGutter>
                {formattedHits.map((product, index) => (
                    <ProductCard
                        key={`${product.productUrl}-${index}`}
                        listType={source}
                        product={product}
                        isSlider
                    />
                ))}
            </ContentSlider>
        );
    }

    // If there are no results, show nothing
    return null;
});

ProductCarousel.displayName = 'ProductCarousel';

export default ProductCarousel;
